import React from "react";
import {Formik, Form, Field, ErrorMessage} from 'formik';
import * as Yup from 'yup';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import axios from 'axios';
import {faLock} from "@fortawesome/pro-thin-svg-icons";

const validationSchema = Yup.object({
    amount: Yup.string()
        .required('Amount is required')
        .matches(/^\d+(\.\d{1,2})?$/, "Invalid amount!")
        .test('max', 'Invalid amount!', value => (parseFloat(value) >= 1 && parseFloat(value) <= 1000000)),

    customer_reference: Yup.string()
        .max(100, 'Payment reference is too long'),

    payment_description: Yup.string()
        .max(200, 'Description is too long'),

    first_name: Yup.string()
        .required('First name is required')
        .max(100, 'First name is too long'),

    last_name: Yup.string()
        .required('Last name is required')
        .max(100, 'Last name is too long'),

    mobile_number: Yup.string()
        .required('Cellphone is required')
        .matches(/^\+?[0-9]{7,15}$/, "Invalid cellphone number"),

    email: Yup.string()
        .required('Email is required')
        .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, 'Invalid email format')
        .max(100, 'Email is too long'),
});


function PaymentPageForm({data, merchantRef, reference, setIsLoading, setErrorStatus, setErrorDetail}) {
    const initialValues = {
        amount: '',
        customer_reference: '',
        payment_description: '',
        first_name: '',
        last_name: '',
        mobile_number: '',
        email: '',
        currency: data?.currency_code
    };
    
    const onSubmit = async (values, {setSubmitting, setErrors}) => {
        setIsLoading(true);
        setErrorStatus(null);
        setErrorDetail(null);
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_HELLOPAY_BASE_URL}v1/merchants/${merchantRef}/payment-pages/${reference}/payment-url`,
                values
            );

            if (response?.data && response?.data.payment_url) {
                window.location.replace(response?.data.payment_url);
            } else {
                throw new Error('Payment URL not received');
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            setErrors({submit: 'An error occurred while processing your payment. Please try again.'});

            if (error.response) {
                setErrorStatus(error?.response.status);
                setErrorDetail(error?.response.data.detail || 'An unexpected error occurred');
            } else {
                setErrorStatus(500);
                setErrorDetail(error?.message || 'An unexpected error occurred');
            }
            setIsLoading(false);
        } finally {
            setSubmitting(false);
        }
    };

    const FormField = ({name, label, type = "text", ...props}) => (
        <div className="form-control my-3">
            <label htmlFor={name}
                   className="block mb-1 font-semibold hellopay_text text-base text-purple">{label}</label>
            <div className="flex border border-purple rounded-lg bg-white p-1 focus-within:border-1">
                <Field
                    type={type}
                    id={name}
                    name={name}
                    autoComplete={name}
                    className=" ml-2 text-sm block w-full px-2.5 py-2 border-0 outline-none"
                    {...props}
                />
            </div>
            <ErrorMessage name={name} component="div" className="error h-px text-soft_red text-sm"/>
        </div>
    );

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
        >
            {({isValid, dirty}) => (
                <Form>
                    <div
                        className="mb-2 p-2 flex rounded-md space-x-2 bg-very_light_purple font-semibold hellopay_text text-purple">
                        <div className="ml-2.5"> {data?.trading_name}</div>
                    </div>

                    {(data?.title || data?.description) &&
                        <div
                            className={`flex rounded-md mb-5 p-1.5 text-purple bg-light_purple`}>
                            <div className="my-2 mx-4 space-y-3">
                                {data?.title != null && data?.title !== '' &&
                                    <p className="text-[20px] font-semibold break-all">{data?.title}</p>
                                }
                                {data?.description != null && data?.description !== '' &&
                                    <p className="text-[18px] break-all">{data?.description}</p>
                                }
                            </div>
                        </div>
                    }

                    <div className="form-control my-3">
                        <label htmlFor="amount"
                               className="block mb-1 font-semibold hellopay_text text-base text-purple"
                        >Amount</label>
                        <div className="flex gap-2">
                            <div
                                className="flex border border-purple rounded-lg bg-white p-1 focus-within:border-1 w-3/4">
                                <Field
                                    type="number"
                                    id="amount"
                                    name="amount"
                                    min="1"
                                    max="1000000"
                                    step="any"
                                    autoComplete="amount"
                                    placeholder="0000.00"
                                    className=" ml-2 text-sm block w-full px-2.5 py-2 border-0 outline-none"
                                />
                            </div>
                            <div className="flex border border-purple rounded-lg bg-gray-100 p-1 w-1/4">
                                <Field
                                    type="text"
                                    id="currency"
                                    name="currency"
                                    disabled
                                    className="ml-2 text-sm block w-full px-2.5 py-2 border-0 outline-none bg-gray-100"
                                />
                            </div>
                        </div>
                        <ErrorMessage name="amount" component="div" className="error h-px text-soft_red text-sm"/>
                    </div>

                    <div className="my-6">
                        <FormField
                            name="customer_reference"
                            label="Payment Reference (Optional)"
                            placeholder="Eg. reference"
                        />
                    </div>

                    <div>
                        <FormField
                            name="payment_description"
                            label="Notes (Optional)"
                            as="textarea"
                            placeholder="Enter notes"
                        />
                    </div>

                    <div className="mt-8 mb-4 space-y-1">
                        <p className="text-center text-purple text-[17px] font-semibold">Your Information</p>
                        <hr className="h-px sm:h-0.5 border-0 bg-medium-purple bg-opacity-50"/>
                    </div>

                    <div className="flex flex-wrap">
                        <div className="w-[100%] lg:w-[50%] lg:pr-2">
                            <FormField
                                name="first_name"
                                label="First Name"
                                placeholder="Enter first name"

                            />
                        </div>
                        <div className="w-[100%] lg:w-[50%] lg:pl-2">
                            <FormField
                                name="last_name"
                                label="Last Name"
                                placeholder="Enter last name"
                            />
                        </div>
                    </div>

                    <div className="flex flex-wrap">
                        <div className="w-[100%] lg:w-[50%] lg:pr-2">
                            <FormField
                                name="mobile_number"
                                label="Cellphone"
                                type="text"
                                placeholder="Enter cellphone number"
                            />
                        </div>
                        <div className="w-[100%] lg:w-[50%] lg:pl-2">
                            <div className="form-control my-3">
                                <label htmlFor="email"
                                       className="block mb-1 font-semibold hellopay_text text-base text-purple">Email</label>
                                <div
                                    className="flex border border-purple rounded-lg bg-white p-1 focus-within:border-1">
                                    <Field
                                        type="email"
                                        id="email"
                                        name="email"
                                        autoComplete="email"
                                        placeholder="Enter email"
                                        className="ml-2 text-sm block w-full px-2.5 py-2 border-0 outline-none"
                                    />
                                </div>
                                <ErrorMessage name="email" component="div"
                                              className="error h-px text-soft_red text-sm"/>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-center my-6">
                        <button
                            type="submit"
                            className={`${
                                !isValid || !dirty
                                    ? 'bg-dark_grayish_lime_green'
                                    : 'bg-gradient-to-r from-purple to-very_dark_grayish_violet'
                            } text-sm h-10 w-80 space-x-2 px-2 rounded-md text-white uppercase`}
                            disabled={!isValid || !dirty}
                        >
                            <FontAwesomeIcon icon={faLock} style={{color: "#f2f2f2"}}/>
                            <span>Proceed to payment</span>
                        </button>
                    </div>
                </Form>
            )}
        </Formik>
    );
}

export default PaymentPageForm;